import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/layout';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { useTranslation } from 'react-i18next';

const AccountBlockedPage: React.FC = () => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const [blockedReason, setBlockedReason] = useState<string>('');
  const [blockedAt, setBlockedAt] = useState<string>('');

  useEffect(() => {
    // Get blocked reason from localStorage
    const reason = localStorage.getItem('blockedReason');
    const timestamp = localStorage.getItem('blockedAt');

    if (reason) {
      setBlockedReason(reason);
      localStorage.removeItem('blockedReason'); // Clean up after reading
    }

    if (timestamp) {
      setBlockedAt(timestamp);
      localStorage.removeItem('blockedAt'); // Clean up after reading
    }

    // Clear any remaining auth data to ensure complete logout
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('lastActivity');
  }, []);

  const handleGoHome = () => {
    navigate('/');
  };

  const formatBlockedDate = (timestamp: string) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  usePageConfig({ title: t('accountBlocked'), showHeader: false, showBottomNav: false, showNavigation: false });

  return (
    <div className="ls-flex ls-align-center ls-justify-center" style={{ minHeight: '80vh' }}>
      <Card className="ls-text-center" style={{ maxWidth: '400px', width: '100%' }}>
          {/* Icon */}
          <div className="ls-mb-4">
            <div
              className="ls-flex ls-align-center ls-justify-center"
              style={{
                width: '80px',
                height: '80px',
                background: 'var(--bg-error)',
                borderRadius: '50%',
                margin: '0 auto'
              }}
            >
              <svg className="theme-text-error" width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h1 className="ls-h2 ls-mb-2">{t('accountBlocked')}</h1>
          <p className="theme-text-secondary ls-mb-4" style={{ fontSize: '13px' }}>
            {t('blockedByAdmin')}
          </p>

          {/* Blocked info */}
          {blockedReason && (
            <div className="theme-bg-error ls-py-3 ls-px-3 ls-mb-4" style={{ borderRadius: '8px', borderLeft: '4px solid var(--text-error)' }}>
              <div className="ls-flex ls-align-start ls-gap-2">
                <svg className="theme-text-error" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="ls-text-left">
                  <p className="ls-label theme-text-error ls-mb-1">{t('blockReason')}:</p>
                  <p className="theme-text-error" style={{ fontSize: '12px' }}>{blockedReason}</p>
                </div>
              </div>
            </div>
          )}

          {blockedAt && (
            <p className="theme-text-muted ls-mb-4" style={{ fontSize: '11px' }}>
              {t('blockedAt')}: {formatBlockedDate(blockedAt)}
            </p>
          )}

          {/* Info box */}
          <div className="theme-bg-tertiary ls-py-3 ls-px-3 ls-mb-4" style={{ borderRadius: '8px' }}>
            <p className="ls-label ls-mb-2">{t('whatThisMeans')}</p>
            <ul className="theme-text-secondary ls-text-left" style={{ fontSize: '12px', listStyle: 'disc', paddingLeft: '16px' }}>
              <li>{t('cannotLogin')}</li>
              <li>{t('accessBlocked')}</li>
              <li>{t('dataPreserved')}</li>
            </ul>
          </div>

          {/* Contact info */}
          <div className="theme-bg-status-success ls-py-3 ls-px-3 ls-mb-4" style={{ borderRadius: '8px' }}>
            <p className="ls-label theme-text-link ls-mb-1">{t('toUnblockAccount')}</p>
            <p className="theme-text-secondary" style={{ fontSize: '12px' }}>
              {t('contactAdmin')}
            </p>
          </div>

          {/* Actions */}
          <button
            onClick={handleGoHome}
            className="ls-btn"
            style={{ width: '100%' }}
          >
            {t('goHome')}
          </button>

          {/* Footer note */}
          <p className="theme-text-muted ls-mt-4 ls-pt-3" style={{ fontSize: '11px', borderTop: '1px solid var(--border-color)' }}>
            {t('contactSupport')}
          </p>
      </Card>
    </div>
  );
};

export default AccountBlockedPage;
